import { useState, useEffect, useRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  Activity,
  FileText,
  Heart,
  Pill,
  AlertTriangle,
  Calendar,
  X,
} from "lucide-react";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import medicalDB from "../services/database";
import DraftSuggestionChips from "./DraftSuggestionChips";
import DraftSelector from "./DraftSelector";
import VisitBillingPopup from "./VisitBillingPopup";
import LongPressNumericPickerInput from "./inputs/LongPressNumericPickerInput";

const VisitForm = ({ patient, visit, onSave, onCancel }) => {
  const isEditing = !!visit;
  const [medicalData, setMedicalData] = useState({
    symptoms: [],
    drugs: [],
    allergens: [],
    routes: [],
    "administered-medications": [],
  });
  const [loading, setLoading] = useState(false);
  const [showDraftSelector, setShowDraftSelector] = useState(false);
  const [appliedDraft, setAppliedDraft] = useState(null);
  const [showSaveAsDraftModal, setShowSaveAsDraftModal] = useState(false);
  const [draftForm, setDraftForm] = useState({
    name: "",
    description: "",
    category: "General",
  });
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [followUpData, setFollowUpData] = useState({
    when: "tomorrow", // "today" or "tomorrow"
    type: "FOLLOW_UP",
    notes: "",
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showNavigationModal, setShowNavigationModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [showBillingPopup, setShowBillingPopup] = useState(false);
  const [pendingVisitData, setPendingVisitData] = useState(null);
  const [visitTypes, setVisitTypes] = useState([]);
  const [medicalActions, setMedicalActions] = useState([]);

  // Refs for auto-focusing on name fields (react-select)
  const takenDrugNameRefs = useRef([]);
  const prescriptionNameRefs = useRef([]);
  const administeredMedNameRefs = useRef([]);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      complaints: visit?.complaints?.map((c) => ({ value: c, label: c })) || [],
      vitalSigns: visit?.vitalSigns || {},
      allergies: visit?.allergies?.map((a) => ({ value: a, label: a })) || [],
      infections: visit?.infections?.map((i) => ({ value: i, label: i })) || [],
      pastHistory:
        visit?.pastHistory?.map((h) => ({ value: h, label: h })) || [],
      notes: visit?.notes || "",
      takenDrugs: visit?.takenDrugs || [],
      prescription: visit?.prescription || [],
      administeredMeds: visit?.administeredMeds || [],
      visitTypeId: visit?.visitTypeId || null,
      performedActions: visit?.performedActions || [],
    },
  });

  // Watch all form values to detect changes
  const watchedValues = watch();

  const {
    fields: takenDrugsFields,
    append: appendTakenDrug,
    remove: removeTakenDrug,
  } = useFieldArray({
    control,
    name: "takenDrugs",
  });

  const {
    fields: prescriptionFields,
    append: appendPrescription,
    remove: removePrescription,
  } = useFieldArray({
    control,
    name: "prescription",
  });

  const {
    fields: administeredMedsFields,
    append: appendAdministeredMed,
    remove: removeAdministeredMed,
  } = useFieldArray({
    control,
    name: "administeredMeds",
  });

  const {
    fields: performedActionsFields,
    append: appendPerformedAction,
    remove: removePerformedAction,
  } = useFieldArray({
    control,
    name: "performedActions",
  });

  useEffect(() => {
    loadMedicalDatasets();
    loadBillingData();
  }, []);

  const loadBillingData = async () => {
    try {
      const [visitTypesData, medicalActionsData] = await Promise.all([
        medicalDB.getAllVisitTypes(),
        medicalDB.getAllMedicalActions(),
      ]);

      setVisitTypes(
        visitTypesData
          .filter((vt) => vt.isActive)
          .map((vt) => ({
            value: vt.id,
            label: `${vt.name} - ₪${vt.defaultPrice}`,
            ...vt,
          }))
      );

      setMedicalActions(
        medicalActionsData
          .filter((ma) => ma.isActive)
          .map((ma) => ({
            value: ma.id,
            label: `${ma.name} - ₪${ma.defaultPrice}`,
            ...ma,
          }))
      );
    } catch (error) {
      console.error("Error loading billing data:", error);
    }
  };

  // Detect unsaved changes
  useEffect(() => {
    const initialValues = JSON.stringify({
      complaints: visit?.complaints?.map((c) => ({ value: c, label: c })) || [],
      vitalSigns: visit?.vitalSigns || {},
      allergies: visit?.allergies?.map((a) => ({ value: a, label: a })) || [],
      infections: visit?.infections?.map((i) => ({ value: i, label: i })) || [],
      pastHistory:
        visit?.pastHistory?.map((h) => ({ value: h, label: h })) || [],
      notes: visit?.notes || "",
      takenDrugs: visit?.takenDrugs || [],
      prescription: visit?.prescription || [],
      administeredMeds: visit?.administeredMeds || [],
      visitTypeId: visit?.visitTypeId || null,
      performedActions: visit?.performedActions || [],
    });

    const currentValues = JSON.stringify(watchedValues);
    setHasUnsavedChanges(initialValues !== currentValues);
  }, [watchedValues, visit]);

  // Prevent navigation with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadMedicalDatasets = async () => {
    try {
      setLoading(true);
      const [symptoms, drugs, allergens, routes, administeredMeds] =
        await Promise.all([
          medicalDB.getMedicalDataset("symptoms"),
          medicalDB.getMedicalDataset("drugs"),
          medicalDB.getMedicalDataset("allergens"),
          medicalDB.getMedicalDataset("routes"),
          medicalDB.getMedicalDataset("administered-medications"),
        ]);

      setMedicalData({
        symptoms: symptoms.map((item) => ({
          value: item.name,
          label: item.name,
        })),
        drugs: drugs.map((item) => ({ value: item.name, label: item.name })),
        allergens: allergens.map((item) => ({
          value: item.name,
          label: item.name,
        })),
        routes: routes.map((item) => ({
          value: item.name,
          label: item.name,
        })),
        "administered-medications": administeredMeds.map((item) => ({
          value: item.name,
          label: item.name,
        })),
      });
    } catch (error) {
      console.error("Error loading medical datasets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewDrug = async (inputValue) => {
    try {
      // Add new drug to the medical dataset
      await medicalDB.addMedicalDatasetItem("drugs", inputValue);

      // Create the new option object
      const newOption = { value: inputValue, label: inputValue };

      // Update the local state to include the new drug
      setMedicalData((prev) => ({
        ...prev,
        drugs: [...prev.drugs, newOption],
      }));

      return newOption;
    } catch (error) {
      console.error("Error adding new drug:", error);
      // Still return the option even if saving fails
      return { value: inputValue, label: inputValue };
    }
  };

  const handleCreateNewAdministeredMed = async (inputValue) => {
    try {
      // Add new administered medication to the medical dataset
      await medicalDB.addMedicalDatasetItem(
        "administered-medications",
        inputValue
      );

      // Create the new option object
      const newOption = { value: inputValue, label: inputValue };

      // Update the local state to include the new administered medication
      setMedicalData((prev) => ({
        ...prev,
        "administered-medications": [
          ...prev["administered-medications"],
          newOption,
        ],
      }));

      return newOption;
    } catch (error) {
      console.error("Error adding new administered medication:", error);
      // Still return the option even if saving fails
      return { value: inputValue, label: inputValue };
    }
  };

  const handleCreateNewRoute = async (inputValue) => {
    try {
      // Add new route to the medical dataset
      await medicalDB.addMedicalDatasetItem("routes", inputValue);

      // Create the new option object
      const newOption = { value: inputValue, label: inputValue };

      // Update the local state to include the new route
      setMedicalData((prev) => ({
        ...prev,
        routes: [...prev.routes, newOption],
      }));

      return newOption;
    } catch (error) {
      console.error("Error adding new route:", error);
      // Still return the option even if saving fails
      return { value: inputValue, label: inputValue };
    }
  };

  const handleSelectDraft = (draft) => {
    try {
      // Apply draft template to form
      applyDraftToForm(draft);
      setAppliedDraft(draft);
      setShowDraftSelector(false);
    } catch (error) {
      console.error("Error applying draft:", error);
      alert("Failed to apply template. Please try again.");
    }
  };

  const applyDraftToForm = (draft) => {
    const template = draft.template;

    // Apply complaints
    if (template.complaints?.length > 0) {
      setValue(
        "complaints",
        template.complaints.map((c) => ({ value: c, label: c }))
      );
    }

    // Apply allergies
    if (template.allergies?.length > 0) {
      setValue(
        "allergies",
        template.allergies.map((a) => ({ value: a, label: a }))
      );
    }

    // Apply infections
    if (template.infections?.length > 0) {
      setValue(
        "infections",
        template.infections.map((i) => ({ value: i, label: i }))
      );
    }

    // Apply past history
    if (template.pastHistory?.length > 0) {
      setValue(
        "pastHistory",
        template.pastHistory.map((h) => ({ value: h, label: h }))
      );
    }

    // Apply notes
    if (template.notes) {
      setValue("notes", template.notes);
    }

    // Apply vital signs (keep structure but don't override with null values)
    if (template.vitalSigns) {
      const currentVitalSigns = watch("vitalSigns") || {};
      const newVitalSigns = { ...currentVitalSigns };

      Object.keys(template.vitalSigns).forEach((key) => {
        if (
          template.vitalSigns[key] !== null &&
          template.vitalSigns[key] !== undefined
        ) {
          newVitalSigns[key] = template.vitalSigns[key];
        }
      });

      setValue("vitalSigns", newVitalSigns);
    }

    // Apply taken drugs
    if (template.takenDrugs?.length > 0) {
      // Clear existing and add template drugs
      setValue("takenDrugs", template.takenDrugs);
    }

    // Apply prescriptions
    if (template.prescription?.length > 0) {
      setValue("prescription", template.prescription);
    }

    // Apply administered medications
    if (template.administeredMeds?.length > 0) {
      setValue("administeredMeds", template.administeredMeds);
    }
  };

  const handleShowAllTemplates = () => {
    setShowDraftSelector(true);
  };

  const handleCloseDraftSelector = () => {
    setShowDraftSelector(false);
  };

  const handleScheduleFollowUp = () => {
    setShowFollowUpModal(true);
  };

  const handleConfirmFollowUp = async () => {
    try {
      const followUpDate = new Date();
      if (followUpData.when === "today") {
        // Keep today's date
      } else if (followUpData.when === "tomorrow") {
        followUpDate.setDate(followUpDate.getDate() + 1);
      }

      // Calculate appointment time (default to 9 AM)
      const startTime = "09:00";
      const endTime = "09:30"; // 30 minutes default

      const appointmentData = {
        patientId: patient.id,
        date: followUpDate.toISOString().split("T")[0],
        startTime: startTime,
        endTime: endTime,
        type: followUpData.type,
        notes:
          followUpData.notes ||
          `Follow-up appointment scheduled after visit on ${new Date().toLocaleDateString()}`,
      };

      await medicalDB.createAppointment(appointmentData);
      setShowFollowUpModal(false);

      // Show success message
      alert(
        `Follow-up appointment scheduled for ${followUpDate.toLocaleDateString()} at ${startTime}`
      );
    } catch (error) {
      console.error("Error scheduling follow-up:", error);
      alert("Failed to schedule follow-up appointment. Please try again.");
    }
  };

  const handleSaveAsDraft = () => {
    // Get current form values
    const currentValues = watch();

    // Generate intelligent name based on form content
    let suggestedName = "Custom Template";
    if (currentValues.complaints?.length > 0) {
      const mainComplaint =
        currentValues.complaints[0]?.label ||
        currentValues.complaints[0]?.value;
      if (mainComplaint) {
        suggestedName = `${mainComplaint} Visit`;
      }
    }

    setDraftForm({
      name: suggestedName,
      description: "",
      category: "General",
    });
    setShowSaveAsDraftModal(true);
  };

  const handleCreateDraftFromForm = async () => {
    try {
      if (!draftForm.name.trim()) {
        alert("Please enter a template name");
        return;
      }

      // Get current form values
      const currentValues = watch();

      // Create draft from current form data
      await medicalDB.createDraftFromVisit(
        {
          complaints:
            currentValues.complaints?.map((c) => c.value || c.label) || [],
          vitalSigns: currentValues.vitalSigns || {},
          allergies:
            currentValues.allergies?.map((a) => a.value || a.label) || [],
          infections:
            currentValues.infections?.map((i) => i.value || i.label) || [],
          pastHistory:
            currentValues.pastHistory?.map((h) => h.value || h.label) || [],
          notes: currentValues.notes || "",
          takenDrugs: currentValues.takenDrugs || [],
          prescription: currentValues.prescription || [],
          administeredMeds: currentValues.administeredMeds || [],
        },
        draftForm.name.trim(),
        draftForm.description.trim(),
        draftForm.category
      );

      setShowSaveAsDraftModal(false);
      setDraftForm({ name: "", description: "", category: "General" });

      // Show success message
      alert(
        `Template "${draftForm.name}" has been saved successfully! You can use it in future visits.`
      );
    } catch (error) {
      console.error("Error creating draft from form:", error);
      alert("Failed to save template. Please try again.");
    }
  };

  const handleCloseSaveAsDraftModal = () => {
    setShowSaveAsDraftModal(false);
    setDraftForm({ name: "", description: "", category: "General" });
  };

  // Navigation confirmation handlers
  const handleNavigationAttempt = (navigationFn) => {
    if (hasUnsavedChanges) {
      setPendingNavigation(() => navigationFn);
      setShowNavigationModal(true);
    } else {
      navigationFn();
    }
  };

  const handleSaveAndNavigate = async () => {
    try {
      // Use the same onSubmit logic but then navigate
      await handleSubmit(async (data) => {
        // Transform and save the data (same logic as onSubmit)
        const visitData = {
          ...data,
          complaints:
            data.complaints
              ?.filter((item) => item && item.value)
              .map((item) => item.value) || [],
          allergies:
            data.allergies
              ?.filter((item) => item && item.value)
              .map((item) => item.value) || [],
          infections:
            data.infections
              ?.filter((item) => item && item.value)
              .map((item) => item.value) || [],
          pastHistory:
            data.pastHistory
              ?.filter((item) => item && item.value)
              .map((item) => item.value) || [],
        };

        // Save to database
        if (isEditing) {
          await medicalDB.updateVisit(visit.id, visitData);
        } else {
          await medicalDB.createVisit(patient.id, visitData);
        }

        // Clear unsaved state and navigate
        setHasUnsavedChanges(false);
        setShowNavigationModal(false);
        if (pendingNavigation) {
          pendingNavigation();
        }
      })();
    } catch (error) {
      console.error("Error saving before navigation:", error);
      alert(`Failed to save visit: ${error.message}`);
    }
  };

  const handleDiscardAndNavigate = () => {
    setHasUnsavedChanges(false);
    setShowNavigationModal(false);
    if (pendingNavigation) {
      pendingNavigation();
    }
  };

  const handleCancelNavigation = () => {
    setShowNavigationModal(false);
    setPendingNavigation(null);
  };

  const hasFormData = () => {
    const values = watch();
    return (
      values.complaints?.length > 0 ||
      values.notes?.trim() ||
      values.allergies?.length > 0 ||
      values.infections?.length > 0 ||
      values.pastHistory?.length > 0 ||
      values.takenDrugs?.length > 0 ||
      values.prescription?.length > 0 ||
      values.administeredMeds?.length > 0 ||
      Object.values(values.vitalSigns || {}).some((v) => v != null && v !== "")
    );
  };

  const categories = [
    "General",
    "Preventive Care",
    "Acute Care",
    "Chronic Care",
    "Emergency",
    "Specialty",
  ];

  const onSubmit = async (data) => {
    try {
      console.log("Form data:", data); // Debug log

      // Transform Select component values back to arrays with proper null checking
      const visitData = {
        ...data,
        complaints:
          data.complaints
            ?.filter((item) => item && item.value)
            .map((item) => item.value) || [],
        allergies:
          data.allergies
            ?.filter((item) => item && item.value)
            .map((item) => item.value) || [],
        infections:
          data.infections
            ?.filter((item) => item && item.value)
            .map((item) => item.value) || [],
        pastHistory:
          data.pastHistory
            ?.filter((item) => item && item.value)
            .map((item) => item.value) || [],
        // Transform performed actions to just store the IDs
        performedActions:
          data.performedActions
            ?.filter((item) => item && item.actionId)
            .map((item) => ({
              actionId: item.actionId,
              customPrice: item.customPrice,
            })) || [],
      };

      console.log("Transformed visit data:", visitData); // Debug log

      // Store the visit data and show billing popup
      setPendingVisitData(visitData);
      setShowBillingPopup(true);
    } catch (error) {
      console.error("Error preparing visit data:", error);
      console.error("Form data at error:", data); // Additional debug info
      alert(
        `Failed to prepare visit data: ${error.message}. Please check the console for details.`
      );
    }
  };

  const handleBillingConfirm = async (transactionData) => {
    try {
      // Save the visit first
      let visitId;
      if (isEditing) {
        await medicalDB.updateVisit(visit.id, {
          ...pendingVisitData,
          billingStatus: medicalDB.BILLING_STATUS?.PROCESSED || "processed",
        });
        visitId = visit.id;
      } else {
        const newVisit = await medicalDB.createVisit(patient.id, {
          ...pendingVisitData,
          billingStatus: medicalDB.BILLING_STATUS?.PROCESSED || "processed",
        });
        visitId = newVisit.id;
      }

      // Save the transaction with the visit ID and patient ID
      await medicalDB.createTransaction({
        ...transactionData,
        patientId: patient.id,
        visitId: visitId,
      });

      // Clear states
      setShowBillingPopup(false);
      setPendingVisitData(null);
      setHasUnsavedChanges(false);

      // Call the onSave callback to close the form and refresh parent data
      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error("Error saving visit and billing:", error);
      alert(
        `Failed to save visit and billing: ${error.message}. Please check the console for details.`
      );
    }
  };

  const handleBillingCancel = () => {
    setShowBillingPopup(false);
    setPendingVisitData(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading medical data...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => handleNavigationAttempt(onCancel)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft size={24} />
        </button>
        <div>
          <h1
            className="text-2xl font-bold text-gray-900"
            data-testid="visit-form-title"
          >
            {isEditing ? "Edit Visit" : "New Visit"}
            {hasUnsavedChanges ? "*" : ""} - {patient.name}
          </h1>
          <p className="text-gray-600">
            {isEditing
              ? "Update visit information"
              : "Record new patient visit details"}
          </p>
        </div>
      </div>

      {/* Draft Template Selection - Only show for new visits */}
      {!isEditing && (
        <DraftSuggestionChips
          onSelectDraft={handleSelectDraft}
          onShowAllTemplates={handleShowAllTemplates}
        />
      )}

      {/* Applied Draft Indicator */}
      {appliedDraft && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <FileText className="h-4 w-4 text-green-600" />
              <span className="text-sm font-medium text-green-900">
                Template Applied: {appliedDraft.name}
              </span>
              <span className="text-xs text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
                {appliedDraft.category}
              </span>
            </div>
            <button
              onClick={() => setAppliedDraft(null)}
              className="text-green-600 hover:text-green-700 text-sm font-medium"
            >
              Clear
            </button>
          </div>
          <p className="text-xs text-green-700 mt-1">
            Form fields have been pre-filled. You can edit any field as needed.
          </p>
        </div>
      )}

      <form
        id="visit-form"
        onSubmit={handleSubmit(onSubmit)}
        className="space-y-6"
        data-testid="visit-form"
      >
        {/* Visit Type & Billing */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Activity className="h-6 w-6 text-medical-blue" />
            <h2 className="text-lg font-semibold text-gray-900">
              Visit Type & Services
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="form-label">Visit Type</label>
              <Controller
                name="visitTypeId"
                control={control}
                render={({ field }) => (
                  <Select
                    options={visitTypes}
                    value={
                      visitTypes.find((vt) => vt.value === field.value) || null
                    }
                    onChange={(selected) =>
                      field.onChange(selected ? selected.value : null)
                    }
                    onBlur={field.onBlur}
                    placeholder="Select visit type..."
                    className="basic-multi-select"
                    classNamePrefix="select"
                    isClearable
                  />
                )}
              />
              <p className="mt-1 text-sm text-gray-600">
                Select the type of visit for billing purposes
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="form-label">Performed Actions</label>
                <button
                  type="button"
                  onClick={() =>
                    appendPerformedAction({ actionId: null, customPrice: null })
                  }
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Plus size={16} />
                  <span>Add Action</span>
                </button>
              </div>

              <div className="space-y-3">
                {performedActionsFields.map((field, index) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end"
                  >
                    <div className="md:col-span-2">
                      <label className="form-label">Medical Action</label>
                      <Controller
                        name={`performedActions.${index}.actionId`}
                        control={control}
                        render={({ field }) => (
                          <Select
                            options={medicalActions}
                            value={
                              medicalActions.find(
                                (ma) => ma.value === field.value
                              ) || null
                            }
                            onChange={(selected) =>
                              field.onChange(selected ? selected.value : null)
                            }
                            onBlur={field.onBlur}
                            placeholder="Select medical action..."
                            className="basic-multi-select"
                            classNamePrefix="select"
                            isClearable
                          />
                        )}
                      />
                    </div>
                    <div className="flex items-end space-x-2">
                      <div className="flex-1">
                        <label className="form-label">Custom Price (₪)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-input"
                          placeholder="Override default price"
                          {...register(`performedActions.${index}.customPrice`)}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removePerformedAction(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
                {performedActionsFields.length === 0 && (
                  <p className="text-gray-500 text-sm">
                    No additional medical actions performed
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Chief Complaints */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="h-6 w-6 text-medical-blue" />
            <h2 className="text-lg font-semibold text-gray-900">
              Chief Complaints
            </h2>
          </div>

          <div>
            <label className="form-label">Symptoms</label>
            <Controller
              name="complaints"
              control={control}
              render={({ field }) => (
                <Select
                  isMulti
                  options={medicalData.symptoms}
                  value={field.value}
                  onChange={(selected) => field.onChange(selected || [])}
                  onBlur={field.onBlur}
                  placeholder="Select or type symptoms..."
                  className="basic-multi-select"
                  classNamePrefix="select"
                />
              )}
            />
            <p className="mt-1 text-sm text-gray-600">
              Select multiple symptoms or type to add custom ones
            </p>
          </div>
        </div>

        {/* Vital Signs */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Heart className="h-6 w-6 text-medical-blue" />
            <h2 className="text-lg font-semibold text-gray-900">Vital Signs</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="form-label">Temperature (°C)</label>
              <Controller
                name="vitalSigns.temp"
                control={control}
                render={({ field }) => (
                  <LongPressNumericPickerInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="36.5"
                    min={35}
                    max={42}
                    step={0.1}
                    decimals={1}
                    unit="°C"
                    inputClassName="form-input"
                  />
                )}
              />
            </div>

            <div>
              <label className="form-label">Blood Pressure</label>
              <input
                type="text"
                className="form-input"
                placeholder="120/80"
                {...register("vitalSigns.bp")}
              />
            </div>

            <div>
              <label className="form-label">Heart Rate (bpm)</label>
              <Controller
                name="vitalSigns.hr"
                control={control}
                render={({ field }) => (
                  <LongPressNumericPickerInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="72"
                    min={30}
                    max={200}
                    step={1}
                    decimals={0}
                    unit="bpm"
                    inputClassName="form-input"
                  />
                )}
              />
            </div>

            <div>
              <label className="form-label">SpO2 (%)</label>
              <Controller
                name="vitalSigns.spo2"
                control={control}
                render={({ field }) => (
                  <LongPressNumericPickerInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="98"
                    min={70}
                    max={100}
                    step={1}
                    decimals={0}
                    unit="%"
                    inputClassName="form-input"
                  />
                )}
              />
            </div>

            <div>
              <label className="form-label">Weight (kg)</label>
              <Controller
                name="vitalSigns.weight"
                control={control}
                render={({ field }) => (
                  <LongPressNumericPickerInput
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="70"
                    min={1}
                    max={300}
                    step={0.1}
                    decimals={1}
                    unit="kg"
                    inputClassName="form-input"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="h-6 w-6 text-medical-blue" />
            <h2 className="text-lg font-semibold text-gray-900">Notes</h2>
          </div>
          <textarea
            className="form-textarea min-h-[200px]"
            placeholder="Add any notes or observations..."
            rows={8}
            {...register("notes")}
          />
          <p className="mt-2 text-sm text-gray-600">
            Add detailed observations, assessment, treatment plans, or any other
            relevant information about this visit.
          </p>
        </div>

        {/* Medical History */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-medical-blue" />
            <h2 className="text-lg font-semibold text-gray-900">
              Medical History
            </h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="form-label">Allergies</label>
              <Controller
                name="allergies"
                control={control}
                render={({ field }) => (
                  <Select
                    isMulti
                    options={medicalData.allergens}
                    value={field.value}
                    onChange={(selected) => field.onChange(selected || [])}
                    onBlur={field.onBlur}
                    placeholder="Select known allergies..."
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                )}
              />
            </div>

            <div>
              <label className="form-label">Infections</label>
              <Controller
                name="infections"
                control={control}
                render={({ field }) => (
                  <Select
                    isMulti
                    options={[
                      { value: "Hepatitis B", label: "Hepatitis B" },
                      { value: "Hepatitis C", label: "Hepatitis C" },
                      { value: "HIV", label: "HIV" },
                      { value: "Tuberculosis", label: "Tuberculosis" },
                    ]}
                    value={field.value}
                    onChange={(selected) => field.onChange(selected || [])}
                    onBlur={field.onBlur}
                    placeholder="Select known infections..."
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                )}
              />
            </div>

            <div>
              <label className="form-label">Past Medical History</label>
              <Controller
                name="pastHistory"
                control={control}
                render={({ field }) => (
                  <Select
                    isMulti
                    options={[
                      { value: "Diabetes", label: "Diabetes" },
                      { value: "Hypertension", label: "Hypertension" },
                      { value: "Heart Disease", label: "Heart Disease" },
                      { value: "Cancer", label: "Cancer" },
                      { value: "Asthma", label: "Asthma" },
                    ]}
                    value={field.value}
                    onChange={(selected) => field.onChange(selected || [])}
                    onBlur={field.onBlur}
                    placeholder="Select past medical conditions..."
                    className="basic-multi-select"
                    classNamePrefix="select"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Current Medications */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Pill className="h-6 w-6 text-medical-blue" />
              <h2 className="text-lg font-semibold text-gray-900">
                Current Medications
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                const newIndex = takenDrugsFields.length;
                appendTakenDrug({ name: "", dose: "", unit: "mg" });
                // Focus on name field after render
                setTimeout(() => {
                  takenDrugNameRefs.current[newIndex]?.focus();
                }, 100);
              }}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Add Drug</span>
            </button>
          </div>

          <div className="space-y-3">
            {takenDrugsFields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
              >
                <div className="md:col-span-2">
                  <label className="form-label">Drug Name</label>
                  <Controller
                    name={`takenDrugs.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <CreatableSelect
                        ref={(el) => (takenDrugNameRefs.current[index] = el)}
                        options={medicalData.drugs}
                        value={
                          medicalData.drugs.find(
                            (drug) => drug.value === field.value
                          ) || null
                        }
                        onChange={(selected) =>
                          field.onChange(selected ? selected.value : "")
                        }
                        onBlur={field.onBlur}
                        onCreateOption={async (inputValue) => {
                          const newOption = await handleCreateNewDrug(
                            inputValue
                          );
                          field.onChange(newOption.value);
                        }}
                        placeholder="Select or type drug name..."
                        className="basic-multi-select"
                        classNamePrefix="select"
                        isClearable
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="form-label">Dose</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="500"
                    {...register(`takenDrugs.${index}.dose`)}
                  />
                </div>
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <label className="form-label">Unit</label>
                    <select
                      className="form-input"
                      {...register(`takenDrugs.${index}.unit`)}
                    >
                      <option value="mg">mg</option>
                      <option value="g">g</option>
                      <option value="ml">ml</option>
                      <option value="tablets">tablets</option>
                      <option value="units">units</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeTakenDrug(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {takenDrugsFields.length === 0 && (
              <p className="text-gray-500 text-sm">
                No current medications recorded
              </p>
            )}
          </div>
        </div>

        {/* Prescription */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Pill className="h-6 w-6 text-medical-green" />
              <h2 className="text-lg font-semibold text-gray-900">
                New Prescription
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                const newIndex = prescriptionFields.length;
                appendPrescription({
                  name: "",
                  dose: "",
                  unit: "mg",
                  duration: "",
                });
                // Focus on name field after render
                setTimeout(() => {
                  prescriptionNameRefs.current[newIndex]?.focus();
                }, 100);
              }}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Add Prescription</span>
            </button>
          </div>

          <div className="space-y-3">
            {prescriptionFields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end"
              >
                <div className="md:col-span-2">
                  <label className="form-label">Drug Name</label>
                  <Controller
                    name={`prescription.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <CreatableSelect
                        ref={(el) => (prescriptionNameRefs.current[index] = el)}
                        options={medicalData.drugs}
                        value={
                          medicalData.drugs.find(
                            (drug) => drug.value === field.value
                          ) || null
                        }
                        onChange={(selected) =>
                          field.onChange(selected ? selected.value : "")
                        }
                        onBlur={field.onBlur}
                        onCreateOption={async (inputValue) => {
                          const newOption = await handleCreateNewDrug(
                            inputValue
                          );
                          field.onChange(newOption.value);
                        }}
                        placeholder="Select or type drug name..."
                        className="basic-multi-select"
                        classNamePrefix="select"
                        isClearable
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="form-label">Dose</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="500"
                    {...register(`prescription.${index}.dose`)}
                  />
                </div>
                <div>
                  <label className="form-label">Unit</label>
                  <select
                    className="form-input"
                    {...register(`prescription.${index}.unit`)}
                  >
                    <option value="mg">mg</option>
                    <option value="g">g</option>
                    <option value="ml">ml</option>
                    <option value="tablets">tablets</option>
                    <option value="units">units</option>
                  </select>
                </div>
                <div className="flex items-end space-x-2">
                  <div className="flex-1">
                    <label className="form-label">Duration</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="7 days"
                      {...register(`prescription.${index}.duration`)}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removePrescription(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {prescriptionFields.length === 0 && (
              <p className="text-gray-500 text-sm">No prescriptions recorded</p>
            )}
          </div>
        </div>

        {/* Administered Medications */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Pill className="h-6 w-6 text-medical-blue" />
              <h2 className="text-lg font-semibold text-gray-900">
                Administered Medications
              </h2>
            </div>
            <button
              type="button"
              onClick={() => {
                const newIndex = administeredMedsFields.length;
                appendAdministeredMed({
                  name: "",
                  dose: "",
                  unit: "mg",
                  route: "",
                  time: new Date().toLocaleTimeString("en-US", {
                    hour12: false,
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                });
                // Focus on name field after render
                setTimeout(() => {
                  administeredMedNameRefs.current[newIndex]?.focus();
                }, 100);
              }}
              className="btn-secondary flex items-center space-x-2"
            >
              <Plus size={16} />
              <span>Add Administered Medication</span>
            </button>
          </div>

          <div className="space-y-3">
            {administeredMedsFields.map((field, index) => (
              <div
                key={field.id}
                className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end"
              >
                <div className="md:col-span-2">
                  <label className="form-label">Drug Name</label>
                  <Controller
                    name={`administeredMeds.${index}.name`}
                    control={control}
                    render={({ field }) => (
                      <CreatableSelect
                        ref={(el) =>
                          (administeredMedNameRefs.current[index] = el)
                        }
                        options={medicalData["administered-medications"]}
                        value={
                          medicalData["administered-medications"].find(
                            (drug) => drug.value === field.value
                          ) || null
                        }
                        onChange={(selected) =>
                          field.onChange(selected ? selected.value : "")
                        }
                        onBlur={field.onBlur}
                        onCreateOption={async (inputValue) => {
                          const newOption =
                            await handleCreateNewAdministeredMed(inputValue);
                          field.onChange(newOption.value);
                        }}
                        placeholder="Select or type drug name..."
                        className="basic-multi-select"
                        classNamePrefix="select"
                        isClearable
                      />
                    )}
                  />
                </div>
                <div>
                  <label className="form-label">Dose</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="5"
                    {...register(`administeredMeds.${index}.dose`)}
                  />
                </div>
                <div>
                  <label className="form-label">Unit</label>
                  <select
                    className="form-input"
                    {...register(`administeredMeds.${index}.unit`)}
                  >
                    <option value="mg">mg</option>
                    <option value="g">g</option>
                    <option value="ml">ml</option>
                    <option value="units">units</option>
                    <option value="mcg">mcg</option>
                  </select>
                </div>
                <div>
                  <label className="form-label">Route</label>
                  <Controller
                    name={`administeredMeds.${index}.route`}
                    control={control}
                    render={({ field }) => (
                      <CreatableSelect
                        options={medicalData.routes}
                        value={
                          medicalData.routes.find(
                            (route) => route.value === field.value
                          ) || null
                        }
                        onChange={(selected) =>
                          field.onChange(selected ? selected.value : "")
                        }
                        onBlur={field.onBlur}
                        onCreateOption={async (inputValue) => {
                          const newOption = await handleCreateNewRoute(
                            inputValue
                          );
                          field.onChange(newOption.value);
                        }}
                        placeholder="Select route..."
                        className="basic-multi-select"
                        classNamePrefix="select"
                        isClearable
                      />
                    )}
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeAdministeredMed(index)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {administeredMedsFields.length === 0 && (
              <p className="text-gray-500 text-sm">
                No medications administered during this visit
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          <div>
            {hasFormData() && (
              <button
                type="button"
                onClick={handleSaveAsDraft}
                className="inline-flex items-center space-x-2 px-4 py-2 text-sm font-medium text-medical-blue bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <FileText size={16} />
                <span>Save as Template</span>
              </button>
            )}
          </div>
          {/* Form actions area - buttons moved to floating group */}
          <div className="flex justify-end">
            {/* Actions moved to floating button group */}
          </div>
        </div>
      </form>

      {/* Floating Button Group */}
      <div className="fixed bottom-6 right-6 z-40">
        <div className="flex flex-col space-y-3">
          {/* Schedule Follow-up Button (only for new visits) */}
          {!isEditing && (
            <button
              type="button"
              onClick={handleScheduleFollowUp}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 shadow-lg hover:shadow-xl"
              data-testid="schedule-followup-button"
            >
              <Calendar size={16} />
              <span>Schedule Follow-up</span>
            </button>
          )}

          {/* Save Button */}
          <button
            type="submit"
            form="visit-form"
            className={`btn-primary shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 ${
              hasUnsavedChanges ? "ring-2 ring-orange-300" : ""
            }`}
            disabled={isSubmitting}
            data-testid="floating-save-button"
          >
            {hasUnsavedChanges && (
              <div
                className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"
                data-testid="unsaved-indicator"
              ></div>
            )}
            <Save size={20} />
            <span>{isSubmitting ? "Saving..." : "Save Visit"}</span>
          </button>
        </div>
      </div>

      {/* Draft Selector Modal */}
      {showDraftSelector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6">
              <DraftSelector
                onSelectDraft={handleSelectDraft}
                onClose={handleCloseDraftSelector}
              />
            </div>
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      {showSaveAsDraftModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Save as Template
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Create a reusable template from this visit form
              </p>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="form-label">Template Name *</label>
                <input
                  type="text"
                  value={draftForm.name}
                  onChange={(e) =>
                    setDraftForm({ ...draftForm, name: e.target.value })
                  }
                  placeholder="e.g., Routine Checkup"
                  className="form-input"
                  required
                />
              </div>

              <div>
                <label className="form-label">Description</label>
                <textarea
                  value={draftForm.description}
                  onChange={(e) =>
                    setDraftForm({ ...draftForm, description: e.target.value })
                  }
                  placeholder="Brief description of when to use this template"
                  rows={3}
                  className="form-input"
                />
              </div>

              <div>
                <label className="form-label">Category</label>
                <select
                  value={draftForm.category}
                  onChange={(e) =>
                    setDraftForm({ ...draftForm, category: e.target.value })
                  }
                  className="form-input"
                >
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <FileText className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">What will be saved:</p>
                    <ul className="text-xs space-y-0.5">
                      <li>
                        • All current form data (complaints, notes, medications,
                        etc.)
                      </li>
                      <li>
                        • Vital signs structure (values will be cleared for
                        reuse)
                      </li>
                      <li>• This template can be used in future visits</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex items-center justify-end space-x-3 rounded-b-lg">
              <button
                onClick={handleCloseSaveAsDraftModal}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateDraftFromForm}
                className="btn-primary"
              >
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Scheduling Modal */}
      {showFollowUpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Schedule Follow-up Appointment
                </h3>
                <button
                  onClick={() => setShowFollowUpModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="form-label">Schedule for</label>
                  <select
                    value={followUpData.when}
                    onChange={(e) =>
                      setFollowUpData({
                        ...followUpData,
                        when: e.target.value,
                      })
                    }
                    className="form-input"
                    data-testid="followup-when-select"
                  >
                    <option value="today">Today</option>
                    <option value="tomorrow">Tomorrow</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Appointment Type</label>
                  <select
                    value={followUpData.type}
                    onChange={(e) =>
                      setFollowUpData({ ...followUpData, type: e.target.value })
                    }
                    className="form-input"
                  >
                    <option value="FOLLOW_UP">Follow-up</option>
                    <option value="ROUTINE">Routine Checkup</option>
                    <option value="CONSULTATION">Consultation</option>
                  </select>
                </div>

                <div>
                  <label className="form-label">Notes (optional)</label>
                  <textarea
                    value={followUpData.notes}
                    onChange={(e) =>
                      setFollowUpData({
                        ...followUpData,
                        notes: e.target.value,
                      })
                    }
                    placeholder="Add any specific instructions for the follow-up..."
                    rows={3}
                    className="form-input"
                  />
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-start space-x-2">
                    <Calendar className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium">
                        Appointment will be scheduled for:
                      </p>
                      <p>
                        {new Date(
                          Date.now() + followUpData.days * 24 * 60 * 60 * 1000
                        ).toLocaleDateString()}{" "}
                        at 9:00 AM
                      </p>
                      <p className="text-xs mt-1">
                        Time can be adjusted later in the appointments section
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex items-center justify-end space-x-3 rounded-b-lg">
              <button
                onClick={() => setShowFollowUpModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button onClick={handleConfirmFollowUp} className="btn-primary">
                Schedule Appointment
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Confirmation Modal */}
      {showNavigationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3
                  className="text-lg font-semibold text-gray-900"
                  data-testid="navigation-modal-title"
                >
                  Unsaved Changes
                </h3>
                <button
                  onClick={handleCancelNavigation}
                  className="text-gray-400 hover:text-gray-600"
                  data-testid="navigation-modal-close"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600">
                  You have unsaved changes. What would you like to do?
                </p>
              </div>

              <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
                <button
                  onClick={handleSaveAndNavigate}
                  className="btn-primary flex-1"
                  data-testid="save-and-navigate-button"
                >
                  Save & Continue
                </button>
                <button
                  onClick={handleDiscardAndNavigate}
                  className="btn-secondary flex-1"
                  data-testid="discard-and-navigate-button"
                >
                  Discard Changes
                </button>
                <button
                  onClick={handleCancelNavigation}
                  className="btn-secondary flex-1"
                  data-testid="cancel-navigation-button"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Popup */}
      {showBillingPopup && pendingVisitData && (
        <VisitBillingPopup
          isOpen={showBillingPopup}
          onClose={handleBillingCancel}
          onConfirm={handleBillingConfirm}
          patient={patient}
          visitData={pendingVisitData}
          visitTypeId={pendingVisitData.visitTypeId}
          performedActions={pendingVisitData.performedActions || []}
        />
      )}
    </div>
  );
};

export default VisitForm;
